import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSystem } from '@ohif/core';
import { Formik, Form, useFormikContext } from 'formik';
import PageLayout from '../../components/layouts/PageLayout';
import '../../theme/common.css';
import {
  FormField,
  FormSelect,
  FormPhoneInput,
  FormSection,
  FormButtonGroup,
  FileUpload,
  ConfirmationModal,
} from '../../components/common';
import FormSelectVirtualized from '../../components/common/FormSelectVirtualized';
import ReportPreviewModal from '../../components/common/ReportPreviewModal';
import { Icons } from '@ohif/ui-next';
import facilityService from '../../services/facilityService';
import { DEFAULT_PACS_IP_ADDRESS } from '../../constants/facilityDefaults';
import locationService from '../../services/locationService';
import { mapApiResponseToFormValues, normalizeFacilityCodeInput } from '../../utils/facilityMapper';
import authService from '../../auth/authService';
import { hasRole, ROLES, getUserRoleFromAuthUser } from '../../utils/roleUtils';
import { GENDER_OPTIONS } from '../../constants/constant';
import type { Facility } from '../../types/facility.types';
import type { User } from '../../types/user.types';

import { getFacilityValidationSchema } from '../../validation';
import { trimFormStringFields } from '../../utils/trimFormStringFields';
import {
  buildFacilityUpdateValues,
  createFacilityTrimBlurHandler,
  executeFacilityCreate,
  executeFacilityUpdate,
  getFacilityFieldError,
  notifyFacility,
  resolveApprovedBySelectValue,
  resolveFacilityIdForUpdate,
} from './addFacilityHelpers';

interface LocationState {
  facility?: Facility | unknown; // Full Facility object from API
  isEdit?: boolean;
}

const DUAL_STAGE_REPORT_APPROVAL_FORM_OPTIONS = [
  { value: 'Yes', label: 'Enabled' },
  { value: 'No', label: 'Disabled' },
] as const;

const APPROVED_BY_ROLE_FORM_OPTIONS = [
  { value: 'Senior Radiologist', label: 'Senior Radiologist' },
  { value: 'QC Technician', label: 'QC Technician' },
] as const;

// Helper function to map facility data to form initial values
const mapFacilityToInitialValues = (facility: Facility | null | undefined) => {
  if (!facility) {
    return {
      // Facility Details
      name: '',
      code: '',
      type: '',
      description: '',
      phoneNo: '',
      emailId: '',

      // Facility Address
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India', // Fixed to India

      // Letterhead Configuration
      logo: null as File | null,
      logoFileName: '',
      headerText: '',
      footerText: '',

      // PACS/RIS Integration - Default values
      pacsAETitle: 'DCM4CHEE',
      pacsIPAddress: DEFAULT_PACS_IP_ADDRESS,
      pacsPort: '11112',
      integrationStatus: 'Pending', // Default to 'Pending'
      status: 'Active', // Default to 'Active'

      // Facility Admin Details
      employeeId: '',
      fullName: '',
      adminPhoneNo: '',
      adminEmailId: '',
      gender: '',
      username: '',
      password: '',
      designation: '',
      hasRadiologistHierarchy: 'No', // Default to 'No'
      seniorRadiologistReviewRequired: 'Senior Radiologist',
    };
  }

  // Check if facility is from API (has facility_name) or old format
  if (facility && 'facility_name' in facility) {
    // It's a full Facility object from API - use the mapper
    // Extract adminUser if it was passed as a property
    const facilityObj = facility as Facility & { adminUser?: User };
    const adminUser = facilityObj.adminUser;
    const mappedValues = mapApiResponseToFormValues(facilityObj, adminUser);
    return {
      // Facility Details
      name: mappedValues.name || '',
      code: mappedValues.code || '',
      type: mappedValues.type || '',
      description: mappedValues.description || '',
      phoneNo: mappedValues.phoneNo || '',
      emailId: mappedValues.emailId || '',

      // Facility Address
      addressLine1: mappedValues.addressLine1 || '',
      addressLine2: mappedValues.addressLine2 || '',
      city: mappedValues.city || '',
      state: mappedValues.state || '',
      pincode: mappedValues.pincode || '',
      country: 'India', // Fixed to India

      // Letterhead Configuration
      logo: null as File | null,
      logoFileName: mappedValues.logoFileName || '',
      headerText: mappedValues.headerText || '',
      footerText: mappedValues.footerText || '',

      // PACS/RIS Integration - Use mapped values or defaults
      pacsAETitle: mappedValues.pacsAETitle || 'DCM4CHEE',
      pacsIPAddress: mappedValues.pacsIPAddress || DEFAULT_PACS_IP_ADDRESS,
      pacsPort: mappedValues.pacsPort || '11112',
      integrationStatus: mappedValues.integrationStatus || 'Pending',
      status: mappedValues.status || 'Active',

      // Facility Admin Details - use mapped values from API response
      employeeId: mappedValues.employeeId || '',
      fullName: mappedValues.fullName || '',
      adminPhoneNo: mappedValues.adminPhoneNo || '',
      adminEmailId: mappedValues.adminEmailId || '',
      gender: mappedValues.gender || '',
      username: mappedValues.username || '',
      password: mappedValues.password || '',
      designation: mappedValues.designation || '',
      hasRadiologistHierarchy: mappedValues.hasRadiologistHierarchy || 'No',
      seniorRadiologistReviewRequired:
        mappedValues.seniorRadiologistReviewRequired || 'Senior Radiologist',
    };
  }

  // Legacy format support (for backward compatibility)
  const legacyFacility = facility as unknown as Record<string, unknown>;
  const adminPhoneNo =
    typeof legacyFacility?.adminPhNo === 'string'
      ? legacyFacility.adminPhNo.replace(/\+91\s*/, '')
      : '';

  return {
    // Facility Details
    name: (typeof legacyFacility?.name === 'string' ? legacyFacility.name : '') || '',
    code: '',
    type: '',
    description: '',
    phoneNo: '',
    emailId: (typeof legacyFacility?.emailId === 'string' ? legacyFacility.emailId : '') || '',

    // Facility Address
    addressLine1: (typeof legacyFacility?.address === 'string' ? legacyFacility.address : '') || '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India', // Fixed to India

    // Letterhead Configuration
    logo: null as File | null,
    logoFileName: '',
    headerText: '',
    footerText: '',

    // PACS/RIS Integration - Default values
    pacsAETitle: 'DCM4CHEE',
    pacsIPAddress: DEFAULT_PACS_IP_ADDRESS,
    pacsPort: '11112',
    integrationStatus:
      (typeof legacyFacility?.integrationStatus === 'string'
        ? legacyFacility.integrationStatus
        : 'Pending') || 'Pending',
    status:
      (typeof legacyFacility?.status === 'string' ? legacyFacility.status : 'Active') || 'Active',

    // Facility Admin Details
    employeeId: '',
    fullName: (typeof legacyFacility?.adminName === 'string' ? legacyFacility.adminName : '') || '',
    adminPhoneNo: adminPhoneNo,
    adminEmailId: '',
    gender: '',
    username: '',
    password: '',
    designation: '',
    hasRadiologistHierarchy: 'No', // Default to 'No'
    seniorRadiologistReviewRequired: 'Senior Radiologist',
  };
};

// Formik initial values type
type FacilityFormInitialValues = ReturnType<typeof mapFacilityToInitialValues>;

interface AddFacilityProps {
  readOnly?: boolean;
  initialFacility?: Facility | null | unknown; // Accept full Facility type from API
  hideHeader?: boolean;
  hideFooter?: boolean;
  isEditable?: boolean;
  setIsEditable?: (value: boolean) => void;
  editButton?: React.ReactNode; // Optional edit button to show in first section
  isAdminView?: boolean; // If true, this is admin viewing their own facility
  onUpdateSuccess?: (updatedFacility: Facility) => void; // Callback after successful update
}

// Component to handle city fetching using Formik context
interface CityFetcherProps {
  setCityOptions: (cities: { value: string; label: string }[]) => void;
  setIsLoadingCities: (loading: boolean) => void;
  skipNextCityFetchRef: React.MutableRefObject<boolean>;
}

const CityFetcher: React.FC<CityFetcherProps> = ({
  setCityOptions,
  setIsLoadingCities,
  skipNextCityFetchRef,
}) => {
  const { values, setFieldValue } = useFormikContext<FacilityFormInitialValues>();
  const previousStateRef = useRef<string | undefined>(undefined);
  const hasInitializedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!values.state) {
      hasInitializedRef.current = false;
      previousStateRef.current = '';
      setCityOptions([]);
      setIsLoadingCities(false);
      return;
    }

    if (skipNextCityFetchRef.current) {
      skipNextCityFetchRef.current = false;
      hasInitializedRef.current = true;
      previousStateRef.current = values.state;
      return;
    }

    // On initial mount, if state is already set, fetch cities
    if (!hasInitializedRef.current && values.state) {
      hasInitializedRef.current = true;
      previousStateRef.current = values.state;

      const fetchCities = async () => {
        setIsLoadingCities(true);
        try {
          const cities = await locationService.getCities(values.state);
          setCityOptions(cities);
        } catch (error) {
          console.error('Failed to fetch cities:', error);
          setCityOptions([]);
        } finally {
          setIsLoadingCities(false);
        }
      };

      fetchCities();
      return;
    }

    // Only fetch if state actually changed
    if (previousStateRef.current === values.state) {
      return;
    }

    previousStateRef.current = values.state;
    const fetchCities = async () => {
      if (!values.state) {
        setCityOptions([]);
        return;
      }

      setIsLoadingCities(true);
      try {
        const cities = await locationService.getCities(values.state);
        setCityOptions(cities);
        // Clear city if it's not in the new list
        if (values.city && !cities.some(c => c.value === values.city)) {
          setFieldValue('city', '', false); // false = don't validate
        }
      } catch (error) {
        console.error('Failed to fetch cities:', error);
        setCityOptions([]);
      } finally {
        setIsLoadingCities(false);
      }
    };

    fetchCities();
  }, [
    values.state,
    values.city,
    setCityOptions,
    setIsLoadingCities,
    setFieldValue,
    skipNextCityFetchRef,
  ]);

  return null;
};

type AddFacilityFormBodyProps = {
  values: any;
  handleChange: any;
  setFieldValue: any;
  formikHandleSubmit: any;
  isValid: boolean;
  formikDirty: boolean;
  trimTextFieldBlur: (fieldName: string) => (e: React.FocusEvent<HTMLInputElement>) => void;
  getFieldError: (fieldName: string) => string | undefined;
  handleCancel: () => void;
  isReportPreviewOpen: boolean;
  setIsReportPreviewOpen: React.Dispatch<React.SetStateAction<boolean>>;
  lastPincodeLookupRef: React.MutableRefObject<string>;
  skipNextCityFetchRef: React.MutableRefObject<boolean>;
  setIsLoadingPincode: React.Dispatch<React.SetStateAction<boolean>>;
  setCityOptions: React.Dispatch<React.SetStateAction<{ value: string; label: string }[]>>;
  setIsLoadingCities: React.Dispatch<React.SetStateAction<boolean>>;
  uiNotificationService: any;
  initialHasRadiologistHierarchy: string;
  isEditable: boolean;
  isEdit: boolean;
  isAdminView: boolean;
  isSuperAdmin: boolean;
  hideFooter: boolean;
  editButton: React.ReactNode;
  facilityTypeOptions: { value: string; label: string }[];
  statusOptions: { value: string; label: string }[];
  stateOptions: { value: string; label: string }[];
  cityOptions: { value: string; label: string }[];
  countryOptions: { value: string; label: string }[];
  integrationStatusOptions: { value: string; label: string }[];
  isLoadingStates: boolean;
  isLoadingCities: boolean;
  isLoadingPincode: boolean;
  locationService: typeof locationService;
  showConfirmationModal: boolean;
  setShowConfirmationModal: React.Dispatch<React.SetStateAction<boolean>>;
  pendingSubmit: (() => void) | null;
  setPendingSubmit: React.Dispatch<React.SetStateAction<(() => void) | null>>;
};

function AddFacilityHierarchyConfirmModal(
  props: Readonly<{
    showConfirmationModal: boolean;
    setShowConfirmationModal: React.Dispatch<React.SetStateAction<boolean>>;
    pendingSubmit: (() => void) | null;
    setPendingSubmit: React.Dispatch<React.SetStateAction<(() => void) | null>>;
  }>
) {
  const { showConfirmationModal, setShowConfirmationModal, pendingSubmit, setPendingSubmit } =
    props;
  return (
    <ConfirmationModal
      isOpen={showConfirmationModal}
      onClose={() => {
        setShowConfirmationModal(false);
        setPendingSubmit(null);
      }}
      onConfirm={() => {
        setShowConfirmationModal(false);
        if (pendingSubmit) {
          pendingSubmit();
          setPendingSubmit(null);
        }
      }}
      title="Disable Report Approval"
      message={`Are you sure you want to disable the Report Approval option?
      \n All reports awaiting review will be approved automatically.`}
      confirmText="Confirm"
      cancelText="Cancel"
      confirmVariant="danger"
    />
  );
}

function AddFacilityFormFooter(
  props: Readonly<{
    hideFooter: boolean;
    handleCancel: () => void;
    formikHandleSubmit: () => void;
    isValid: boolean;
    formikDirty: boolean;
    isEdit: boolean;
  }>
) {
  const { hideFooter, handleCancel, formikHandleSubmit, isValid, formikDirty, isEdit } = props;
  if (hideFooter) {
    return null;
  }
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: '84px',
        padding: '16px',
        backgroundColor: 'var(--bg-primary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '8px',
        zIndex: 100,
      }}
    >
      <button
        type="button"
        onClick={handleCancel}
        className="btn btn-secondary btn-sm btn-inline"
      >
        Cancel
      </button>
      <button
        type="submit"
        onClick={e => {
          e.preventDefault();
          formikHandleSubmit();
        }}
        disabled={!isValid || !formikDirty}
        className="btn btn-primary btn-sm btn-inline"
      >
        {isEdit ? 'Update Facility' : 'Add Facility'}
      </button>
    </div>
  );
}

function AddFacilityFormBody({
  values,
  handleChange,
  setFieldValue,
  formikHandleSubmit,
  isValid,
  formikDirty,
  trimTextFieldBlur,
  getFieldError,
  handleCancel,
  isReportPreviewOpen,
  setIsReportPreviewOpen,
  lastPincodeLookupRef,
  skipNextCityFetchRef,
  setIsLoadingPincode,
  setCityOptions,
  setIsLoadingCities,
  uiNotificationService,
  initialHasRadiologistHierarchy,
  isEditable,
  isEdit,
  isAdminView,
  isSuperAdmin,
  hideFooter,
  editButton,
  facilityTypeOptions,
  statusOptions,
  stateOptions,
  cityOptions,
  countryOptions,
  integrationStatusOptions,
  isLoadingStates,
  isLoadingCities,
  isLoadingPincode,
  locationService,
  showConfirmationModal,
  setShowConfirmationModal,
  pendingSubmit,
  setPendingSubmit,
}: Readonly<AddFacilityFormBodyProps>) {
  const pincodeLookupGenRef = useRef(0);
  return (
    <>
      <CityFetcher
        setCityOptions={setCityOptions}
        setIsLoadingCities={setIsLoadingCities}
        skipNextCityFetchRef={skipNextCityFetchRef}
      />
      <Form>
        <div
          style={{
            padding: '0',
            backgroundColor: 'var(--bg-primary)',
            minHeight: 'calc(100vh - 56px)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Form Content */}
          <div
            style={{
              flex: 1,
              paddingBottom: '84px',
            }}
          >
            {/* Section 1: Facility Details */}
            <FormSection
              number={1}
              title="Facility Details"
              rightAction={editButton}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '16px',
                  marginBottom: '16px',
                }}
              >
                <FormField
                  label="Name"
                  name="name"
                  required
                  value={values.name}
                  onChange={handleChange}
                  onBlur={trimTextFieldBlur('name')}
                  placeholder="Enter"
                  disabled={!isEditable}
                  error={getFieldError('name')}
                />
                <FormField
                  label="Code"
                  name="code"
                  required
                  value={values.code}
                  onChange={e => {
                    setFieldValue('code', normalizeFacilityCodeInput(e.target.value));
                  }}
                  onBlur={trimTextFieldBlur('code')}
                  placeholder="Enter"
                  disabled={!isEditable}
                  maxLength={20}
                  error={getFieldError('code')}
                />
                <FormSelect
                  label="Type"
                  name="type"
                  required
                  value={values.type}
                  onChange={handleChange}
                  options={facilityTypeOptions}
                  placeholder="Select Type"
                  disabled={!isEditable}
                  error={getFieldError('type')}
                />
                <FormField
                  label="Description"
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={trimTextFieldBlur('description')}
                  placeholder="Enter"
                  disabled={!isEditable}
                  error={getFieldError('description')}
                />
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '16px',
                }}
              >
                <FormPhoneInput
                  label="Phone No."
                  required
                  value={values.phoneNo}
                  onChange={value => setFieldValue('phoneNo', value)}
                  placeholder="Enter"
                  disabled={!isEditable}
                  error={getFieldError('phoneNo')}
                />
                <FormField
                  label="Email ID"
                  name="emailId"
                  type="email"
                  required
                  value={values.emailId}
                  onChange={handleChange}
                  onBlur={trimTextFieldBlur('emailId')}
                  placeholder="Enter"
                  disabled={!isEditable || isAdminView} // Disable email for admin view
                  error={getFieldError('emailId')}
                />
                <FormSelect
                  label="Status"
                  name="status"
                  required
                  value={values.status}
                  onChange={handleChange}
                  options={statusOptions}
                  placeholder="Select Status"
                  disabled={!isEditable}
                  error={getFieldError('status')}
                />
                <FormSelect
                  label="Dual stage report approval"
                  name="hasRadiologistHierarchy"
                  required
                  value={values.hasRadiologistHierarchy}
                  onChange={e => {
                    const value = e.target.value;
                    setFieldValue('hasRadiologistHierarchy', value);
                    if (value === 'No') {
                      setFieldValue('seniorRadiologistReviewRequired', 'Senior Radiologist');
                    }
                    if (
                      isEdit &&
                      initialHasRadiologistHierarchy === 'No' &&
                      value === 'Yes' &&
                      uiNotificationService
                    ) {
                      uiNotificationService.show({
                        title: 'Warning',
                        message:
                          'You are enabling the Report Approval Flow. Please update the sub-roles of radiologists in the Users page to ensure proper workflow.',
                        type: 'warning',
                        duration: 6000,
                      });
                    }
                  }}
                  options={[...DUAL_STAGE_REPORT_APPROVAL_FORM_OPTIONS]}
                  placeholder="Select"
                  disabled={!isEditable}
                  error={getFieldError('hasRadiologistHierarchy')}
                />
              </div>
              {values.hasRadiologistHierarchy === 'Yes' ? (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '16px',
                    marginTop: '16px',
                  }}
                >
                  <FormSelect
                    label="Approved by"
                    name="seniorRadiologistReviewRequired"
                    required
                    value={resolveApprovedBySelectValue(values.seniorRadiologistReviewRequired, [
                      ...APPROVED_BY_ROLE_FORM_OPTIONS,
                    ])}
                    onChange={handleChange}
                    options={[...APPROVED_BY_ROLE_FORM_OPTIONS]}
                    placeholder="Select"
                    disabled={!isEditable}
                    error={getFieldError('seniorRadiologistReviewRequired')}
                  />
                </div>
              ) : null}
            </FormSection>

            {/* Section 2: Facility Address */}
            <FormSection
              number={2}
              title="Facility Address"
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '24px',
                  marginBottom: '24px',
                }}
              >
                <FormField
                  label="Address Line 1"
                  name="addressLine1"
                  required
                  value={values.addressLine1}
                  onChange={handleChange}
                  onBlur={trimTextFieldBlur('addressLine1')}
                  placeholder="Enter"
                  disabled={!isEditable}
                  error={getFieldError('addressLine1')}
                />
                <FormField
                  label="Address Line 2"
                  name="addressLine2"
                  value={values.addressLine2}
                  onChange={handleChange}
                  onBlur={trimTextFieldBlur('addressLine2')}
                  placeholder="Enter"
                  disabled={!isEditable}
                  error={getFieldError('addressLine2')}
                />
                <FormSelectVirtualized
                  label="State"
                  name="state"
                  required
                  value={values.state}
                  onChange={handleChange}
                  options={stateOptions}
                  placeholder={isLoadingStates ? 'Loading...' : 'Select'}
                  disabled={!isEditable || isLoadingStates || isLoadingPincode}
                  error={getFieldError('state')}
                />
                <FormSelectVirtualized
                  label="City"
                  name="city"
                  required
                  value={values.city}
                  onChange={handleChange}
                  options={cityOptions}
                  placeholder={
                    values.state && (isLoadingCities || isLoadingPincode)
                      ? 'Loading...'
                      : values.state
                        ? 'Select'
                        : 'Select State first'
                  }
                  disabled={!isEditable || !values.state || isLoadingCities || isLoadingPincode}
                  error={getFieldError('city')}
                />
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '24px',
                }}
              >
                <FormField
                  label="Pincode"
                  name="pincode"
                  type="text"
                  required
                  value={values.pincode}
                  onChange={e => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setFieldValue('pincode', value).catch(() => undefined);
                    if (value.length < 6) {
                      lastPincodeLookupRef.current = '';
                      pincodeLookupGenRef.current += 1;
                      setIsLoadingPincode(false);
                      return;
                    }
                    if (value.length === 6 && value !== lastPincodeLookupRef.current) {
                      lastPincodeLookupRef.current = value;
                      const lookupGen = pincodeLookupGenRef.current + 1;
                      pincodeLookupGenRef.current = lookupGen;
                      setIsLoadingPincode(true);
                      void locationService
                        .populateAddressFromPincode(value, stateOptions, {
                          setFieldValue: (field, nextValue, shouldValidate) => {
                            if (pincodeLookupGenRef.current !== lookupGen) {
                              return;
                            }
                            setFieldValue(field, nextValue, shouldValidate).catch(() => undefined);
                          },
                          setCityOptions: options => {
                            if (pincodeLookupGenRef.current === lookupGen) {
                              setCityOptions(options);
                            }
                          },
                          setIsLoadingCities: loading => {
                            if (pincodeLookupGenRef.current === lookupGen) {
                              setIsLoadingCities(loading);
                            }
                          },
                          skipNextCityFetch: skipNextCityFetchRef,
                          isActive: () => pincodeLookupGenRef.current === lookupGen,
                        })
                        .finally(() => {
                          if (pincodeLookupGenRef.current === lookupGen) {
                            setIsLoadingPincode(false);
                          }
                        });
                    }
                  }}
                  onBlur={trimTextFieldBlur('pincode')}
                  placeholder="Enter 6-digit pincode"
                  disabled={!isEditable}
                  maxLength={6}
                  inputMode="numeric"
                  autoComplete="postal-code"
                  error={getFieldError('pincode')}
                />
                <FormSelect
                  label="Country"
                  name="country"
                  required
                  value={values.country}
                  onChange={handleChange}
                  options={countryOptions}
                  placeholder="Select"
                  disabled={true}
                  error={getFieldError('country')}
                />
              </div>
            </FormSection>

            {/* Section 3: Letterhead Configuration - Only show for admin view, hide for super admin */}
            {(isAdminView || (isEdit && !isSuperAdmin)) && (
              <FormSection
                number={3}
                title="Letterhead Configuration"
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '24px',
                  }}
                >
                  <FileUpload
                    label="Logo"
                    value={values.logo?.name || values.logoFileName || ''}
                    onChange={file => {
                      setFieldValue('logo', file);
                      // Clear filename when new file is uploaded
                      if (file) {
                        setFieldValue('logoFileName', '');
                      }
                    }}
                    disabled={!isEditable}
                  />
                  <FormField
                    label="Header Text"
                    name="headerText"
                    value={isAdminView ? values.name : values.headerText}
                    onChange={isAdminView ? undefined : handleChange}
                    onBlur={isAdminView ? undefined : trimTextFieldBlur('headerText')}
                    placeholder="Enter"
                    disabled={!isEditable || isAdminView}
                  />
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <div
                      style={{ flex: 1 }}
                      title={
                        isAdminView
                          ? [
                              values.phoneNo,
                              values.emailId,
                              values.addressLine1,
                              values.addressLine2,
                              values.city,
                              values.state,
                              values.pincode,
                              'India',
                            ]
                              .filter(Boolean)
                              .join(', ')
                          : values.footerText
                      }
                    >
                      <FormField
                        label="Footer Text"
                        name="footerText"
                        value={
                          isAdminView
                            ? [
                                values.phoneNo,
                                values.emailId,
                                values.addressLine1,
                                values.addressLine2,
                                values.city,
                                values.state,
                                values.pincode,
                                'India',
                              ]
                                .filter(Boolean)
                                .join(', ')
                            : values.footerText
                        }
                        onChange={isAdminView ? undefined : handleChange}
                        onBlur={isAdminView ? undefined : trimTextFieldBlur('footerText')}
                        placeholder="Enter"
                        disabled={!isEditable || isAdminView}
                      />
                    </div>
                    {/* Report preview trigger */}
                    <button
                      type="button"
                      onClick={() => setIsReportPreviewOpen(true)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 12px',
                        height: '36px',
                        borderRadius: '4px',
                        border: '1px solid #334155',
                        backgroundColor: '#020617',
                        color: 'var(--text-input-default)',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        marginTop: '22px', // align with input field
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Report Preview
                    </button>
                  </div>
                </div>
              </FormSection>
            )}

            {/* Section 4: PACS/RIS Integration */}
            <FormSection
              number={isAdminView || isEdit ? 4 : 3}
              title="PACS/RIS Integration"
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '16px',
                }}
              >
                <FormField
                  label="PACS AE Title"
                  name="pacsAETitle"
                  value={values.pacsAETitle}
                  onChange={e => {
                    const next = e.target.value
                      .toUpperCase()
                      .replaceAll(/[^A-Z0-9_]/g, '')
                      .slice(0, 16);
                    setFieldValue('pacsAETitle', next);
                  }}
                  onBlur={trimTextFieldBlur('pacsAETitle')}
                  placeholder="Enter"
                  disabled={!isEditable}
                  error={getFieldError('pacsAETitle')}
                />
                <FormField
                  label="PACS IP Address"
                  name="pacsIPAddress"
                  value={values.pacsIPAddress}
                  onChange={handleChange}
                  onBlur={trimTextFieldBlur('pacsIPAddress')}
                  placeholder="Enter"
                  disabled={!isEditable}
                  error={getFieldError('pacsIPAddress')}
                />
                <FormField
                  label="PACS Port"
                  name="pacsPort"
                  value={values.pacsPort}
                  onChange={e => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 5);
                    setFieldValue('pacsPort', digits);
                  }}
                  onBlur={trimTextFieldBlur('pacsPort')}
                  placeholder="Enter"
                  disabled={!isEditable}
                  error={getFieldError('pacsPort')}
                />
                <FormSelect
                  label="Integration Status"
                  name="integrationStatus"
                  required
                  value={values.integrationStatus}
                  onChange={handleChange}
                  options={integrationStatusOptions}
                  placeholder="Select Status"
                  disabled={!isEditable}
                  error={getFieldError('integrationStatus')}
                />
              </div>
            </FormSection>

            {/* Section 5: Facility Admin Details - Hide for admin view */}
            {!isAdminView && (
              <FormSection
                number={5}
                title="Facility Admin Details"
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '16px',
                    marginBottom: '16px',
                  }}
                >
                  <FormField
                    label="Employee ID"
                    name="employeeId"
                    required
                    value={values.employeeId}
                    onChange={e => {
                      const next = e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 20);
                      setFieldValue('employeeId', next);
                    }}
                    onBlur={trimTextFieldBlur('employeeId')}
                    placeholder="Enter"
                    disabled={!isEditable}
                    maxLength={20}
                    error={getFieldError('employeeId')}
                  />
                  <FormField
                    label="Full Name"
                    name="fullName"
                    required
                    value={values.fullName}
                    onChange={handleChange}
                    onBlur={trimTextFieldBlur('fullName')}
                    placeholder="Enter"
                    disabled={!isEditable}
                    error={getFieldError('fullName')}
                  />
                  <FormPhoneInput
                    label="Phone No."
                    required
                    value={values.adminPhoneNo}
                    onChange={value => setFieldValue('adminPhoneNo', value)}
                    placeholder="Enter"
                    disabled={!isEditable}
                    error={getFieldError('adminPhoneNo')}
                  />
                  <FormField
                    label="Email ID"
                    name="adminEmailId"
                    type="email"
                    required
                    value={values.adminEmailId}
                    onChange={handleChange}
                    onBlur={trimTextFieldBlur('adminEmailId')}
                    placeholder="Enter"
                    disabled={isEdit || !isEditable}
                    error={getFieldError('adminEmailId')}
                  />
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '16px',
                  }}
                >
                  <FormButtonGroup
                    label="Gender"
                    required
                    options={GENDER_OPTIONS}
                    value={values.gender}
                    onChange={value => setFieldValue('gender', value)}
                    disabled={!isEditable}
                    error={getFieldError('gender')}
                  />
                  <FormField
                    label="Username"
                    name="username"
                    required
                    value={values.username}
                    onChange={e => {
                      const next = e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9._]/g, '')
                        .slice(0, 30);
                      setFieldValue('username', next);
                    }}
                    onBlur={trimTextFieldBlur('username')}
                    placeholder="Enter"
                    disabled={isEdit || !isEditable}
                    maxLength={30}
                    error={getFieldError('username')}
                  />
                  <FormField
                    label="Designation"
                    name="designation"
                    value={values.designation}
                    onChange={e => {
                      const v = e.target.value.slice(0, 50);
                      setFieldValue('designation', v);
                    }}
                    onBlur={trimTextFieldBlur('designation')}
                    placeholder="Enter Designation"
                    disabled={!isEditable}
                    maxLength={50}
                    error={getFieldError('designation')}
                  />
                  {!isEdit && (
                    <FormField
                      label="Password"
                      name="password"
                      type="password"
                      required={!isEdit}
                      value={values.password}
                      onChange={handleChange}
                      onBlur={trimTextFieldBlur('password')}
                      onFocus={e => {
                        // Clear placeholder when user focuses on the field
                        if (values.password === '••••••') {
                          setFieldValue('password', '');
                        }
                      }}
                      placeholder="Enter Password"
                      disabled={!isEditable}
                      error={getFieldError('password')}
                      rightIcon={
                        <Icons.EyeVisible
                          style={{
                            color: '#334155',
                            cursor: 'pointer',
                            width: '20px',
                            height: '20px',
                          }}
                        />
                      }
                    />
                  )}
                </div>
              </FormSection>
            )}
          </div>

          <AddFacilityFormFooter
            hideFooter={hideFooter}
            handleCancel={handleCancel}
            formikHandleSubmit={formikHandleSubmit}
            isValid={isValid}
            formikDirty={formikDirty}
            isEdit={isEdit}
          />
        </div>
      </Form>

      {/* Report Preview Modal */}
      <ReportPreviewModal
        isOpen={isReportPreviewOpen}
        onClose={() => setIsReportPreviewOpen(false)}
        facilityData={{
          facility_name: values.name,
          facility_description: values.description,
          facility_type: values.type,
          contact_number: values.phoneNo,
          email_id: values.emailId,
          address_line_1: values.addressLine1,
          address_line_2: values.addressLine2,
          city: values.city,
          state: values.state,
          pincode: values.pincode,
          country: values.country,
          letterhead_logo: values.logoFileName || null, // DB filename (if saved)
        }}
        logoFile={values.logo} // Recently uploaded file (if not yet saved)
      />

      <AddFacilityHierarchyConfirmModal
        showConfirmationModal={showConfirmationModal}
        setShowConfirmationModal={setShowConfirmationModal}
        pendingSubmit={pendingSubmit}
        setPendingSubmit={setPendingSubmit}
      />
    </>
  );
}

const AddFacility: React.FC<AddFacilityProps> = ({
  readOnly = false,
  initialFacility,
  hideHeader = false,
  hideFooter = false,
  isEditable: externalIsEditable,
  setIsEditable: externalSetIsEditable,
  editButton,
  isAdminView = false,
  onUpdateSuccess,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { servicesManager } = useSystem();
  const { uiNotificationService } = servicesManager.services;
  const locationState = location.state as LocationState | null;
  const facilityData = (initialFacility || locationState?.facility) as Facility | null | undefined;
  const isEdit = locationState?.isEdit || !!initialFacility || false;

  // Check if current user is super admin or admin
  const currentUser = authService.getUser();
  const isSuperAdmin = currentUser ? hasRole(currentUser, ROLES.SUPER_ADMIN) : false;
  const userRole = currentUser ? getUserRoleFromAuthUser(currentUser) : null;
  const isAdmin = userRole === 'Admin' && !isSuperAdmin;

  // State to manage if fields are editable - use external state if provided, otherwise internal
  const [internalIsEditable, setInternalIsEditable] = useState(!readOnly);
  const isEditable = externalIsEditable !== undefined ? externalIsEditable : internalIsEditable;
  const setIsEditable = externalSetIsEditable || setInternalIsEditable;

  // State for report preview modal
  const [isReportPreviewOpen, setIsReportPreviewOpen] = useState(false);

  // State for confirmation modal
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState<(() => void) | null>(null);

  const initialValues = mapFacilityToInitialValues(facilityData);
  const initialHasRadiologistHierarchy = initialValues.hasRadiologistHierarchy;

  const handleCancel = () => {
    if (setIsEditable && hideFooter) {
      // If we're in FacilityDetails view, reset to read-only mode
      setIsEditable(false);
    } else {
      navigate('/facilities');
    }
  };

  const handleSubmit = async (rawValues: typeof initialValues) => {
    const values = trimFormStringFields(
      rawValues as Record<string, unknown>
    ) as typeof initialValues;
    try {
      if (isEdit) {
        // Check if hasRadiologistHierarchy is being changed from 'Yes' to 'No'
        const isDisablingHierarchy =
          initialHasRadiologistHierarchy === 'Yes' && values.hasRadiologistHierarchy === 'No';

        if (isDisablingHierarchy) {
          // Show confirmation modal
          setPendingSubmit(() => () => performUpdate(values));
          setShowConfirmationModal(true);
          return;
        }
      }

      // Proceed with update/create
      await performUpdate(values);
    } catch (error) {
      console.error('Error submitting facility form:', error);
      // Show error notification
      if (uiNotificationService) {
        uiNotificationService.show({
          title: 'Error',
          message: error instanceof Error ? error.message : 'An unexpected error occurred',
          type: 'error',
          duration: 5000,
        });
      }
    }
  };

  const performUpdate = async (values: typeof initialValues) => {
    try {
      if (!isEdit) {
        await executeFacilityCreate({
          facilityService,
          uiNotificationService,
          values,
          navigate,
        });
        return;
      }

      const locationFacility = locationState?.facility as { id?: number | string } | undefined;
      const initFacility = initialFacility as { id?: number | string } | undefined;
      const facilityId = resolveFacilityIdForUpdate(locationFacility, initFacility);

      if (!facilityId) {
        console.error('Facility ID is required for update');
        return;
      }

      const valuesToSubmit = buildFacilityUpdateValues(values, isAdminView);
      await executeFacilityUpdate({
        facilityService,
        authService,
        uiNotificationService,
        facilityId,
        valuesToSubmit,
        isEdit,
        initialHasRadiologistHierarchy,
        isAdminView,
        hideFooter,
        setIsEditable,
        onUpdateSuccess,
        isAdmin,
        navigate,
      });
    } catch (error) {
      console.error('Error submitting facility form:', error);
      notifyFacility(
        uiNotificationService,
        false,
        '',
        error instanceof Error ? error.message : 'An unexpected error occurred'
      );
    }
  };

  const facilityTypeOptions = [
    { value: 'Hospital', label: 'Hospital' },
    { value: 'Diagnostic Center', label: 'Diagnostic Center' },
    { value: 'Clinic', label: 'Clinic' },
    { value: 'HUB', label: 'Hub' },
    { value: 'SPOKE', label: 'Spoke' },
  ];

  // State and city options from API
  const [stateOptions, setStateOptions] = useState<{ value: string; label: string }[]>([]);
  const [cityOptions, setCityOptions] = useState<{ value: string; label: string }[]>([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [isLoadingPincode, setIsLoadingPincode] = useState(false);
  const skipNextCityFetchRef = useRef(false);
  const lastPincodeLookupRef = useRef('');

  // Fetch states on component mount
  useEffect(() => {
    const fetchStates = async () => {
      setIsLoadingStates(true);
      try {
        const states = await locationService.getStates();
        setStateOptions(states);
      } catch (error) {
        console.error('Failed to fetch states:', error);
      } finally {
        setIsLoadingStates(false);
      }
    };

    fetchStates();
  }, []);

  // Country is fixed to India
  const countryOptions = [{ value: 'India', label: 'India' }];

  const integrationStatusOptions = [
    { value: 'Connected', label: 'Connected' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Failed', label: 'Failed' },
  ];

  const statusOptions = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
    { value: 'Suspended', label: 'Suspended' },
  ];

  const formContent = (
    <Formik
      initialValues={initialValues}
      validationSchema={readOnly ? undefined : getFacilityValidationSchema(isEdit, isAdminView)}
      onSubmit={handleSubmit}
      enableReinitialize={true}
      validateOnMount={true}
      validateOnChange={true}
      validateOnBlur={true}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        handleSubmit: formikHandleSubmit,
        isValid,
        dirty: formikDirty,
      }) => {
        const trimTextFieldBlur = (fieldName: string) =>
          createFacilityTrimBlurHandler(
            fieldName,
            values as Record<string, unknown>,
            handleBlur,
            setFieldValue,
            normalizeFacilityCodeInput
          );

        const getFieldError = (fieldName: string) =>
          getFacilityFieldError(
            errors as Record<string, unknown>,
            touched as Record<string, unknown>,
            values as Record<string, unknown>,
            fieldName
          );
        return (
          <AddFacilityFormBody
            values={values}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            formikHandleSubmit={formikHandleSubmit}
            isValid={isValid}
            formikDirty={formikDirty}
            trimTextFieldBlur={trimTextFieldBlur}
            getFieldError={getFieldError}
            handleCancel={handleCancel}
            isReportPreviewOpen={isReportPreviewOpen}
            setIsReportPreviewOpen={setIsReportPreviewOpen}
            lastPincodeLookupRef={lastPincodeLookupRef}
            skipNextCityFetchRef={skipNextCityFetchRef}
            setIsLoadingPincode={setIsLoadingPincode}
            setCityOptions={setCityOptions}
            setIsLoadingCities={setIsLoadingCities}
            uiNotificationService={uiNotificationService}
            initialHasRadiologistHierarchy={initialHasRadiologistHierarchy}
            isEditable={isEditable}
            isEdit={isEdit}
            isAdminView={isAdminView}
            isSuperAdmin={isSuperAdmin}
            hideFooter={hideFooter}
            editButton={editButton}
            facilityTypeOptions={facilityTypeOptions}
            statusOptions={statusOptions}
            stateOptions={stateOptions}
            cityOptions={cityOptions}
            countryOptions={countryOptions}
            integrationStatusOptions={integrationStatusOptions}
            isLoadingStates={isLoadingStates}
            isLoadingCities={isLoadingCities}
            isLoadingPincode={isLoadingPincode}
            showConfirmationModal={showConfirmationModal}
            setShowConfirmationModal={setShowConfirmationModal}
            pendingSubmit={pendingSubmit}
            setPendingSubmit={setPendingSubmit}
            locationService={locationService}
          />
        );
      }}
    </Formik>
  );

  if (hideHeader) {
    return formContent;
  }

  return (
    <PageLayout
      title={isEdit ? 'Edit Facility' : 'Add New Facility'}
      onBack={() => navigate('/facilities')}
    >
      {formContent}
    </PageLayout>
  );
};

export default AddFacility;
